/**
 * Cprofileimage Component
 * Displays a circular profile image with accent border
 */
'use client';
import type { ReactNode, CSSProperties, ReactElement } from 'react';
import { useState } from 'react';
import { ButtonPreset } from '@/constants/componentPresets';
import { Cbutton } from '../Cbutton';
import { Cimage } from '../Cimage';
import { ColorType, getColorValue } from '@/theme/colors';
/**
 * The member placeholder plate. Exported because it is no longer only this
 * component's default: it is the RULED answer for any person-shaped identity
 * whose picture is missing or dead (2026-08-22), so the result-card families
 * hand it to the kit's identity mark. One asset, one path, one place.
 */
export const DEFAULT_PROFILE_IMAGE_SRC = '/profile image placeholder.png';
const DEFAULT_PROFILE_ALT = 'Profile';
const USER_SELECT_NONE = 'none';
const LOCAL_SRC_PREFIX = '/';
function isLocalImageSrc(src: string): boolean {
    return src.startsWith(LOCAL_SRC_PREFIX);
}
function getProfileImageBorder(input: {
    borderWidth: number;
    borderOpacity: number;
    isHovered: boolean;
}): string {
    const opacity = input.isHovered
        ? Math.min(input.borderOpacity * 1.6, 1)
        : input.borderOpacity;
    return `${input.borderWidth}px solid ${getColorValue(ColorType.Accent, opacity)}`;
}
function getProfileImageWrapperStyle(input: {
    size: number;
    borderWidth: number;
    borderOpacity: number;
    isHovered: boolean;
    hasClick: boolean;
}): CSSProperties {
    return {
        width: `${input.size}px`,
        height: `${input.size}px`,
        borderRadius: '50%',
        border: getProfileImageBorder(input),
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'all 200ms ease-in-out',
        userSelect: USER_SELECT_NONE,
    };
}
function getProfileTooltipTransform(isHovered: boolean): string {
    if (isHovered) {
        return 'translateX(-50%) translateY(0)';
    }
    return 'translateX(-50%) translateY(-10px)';
}
function getProfileTooltipStyle(input: {
    size: number;
    isHovered: boolean;
}): CSSProperties {
    return {
        position: 'absolute',
        top: '100%',
        left: '50%',
        marginTop: `${input.size * 0.15}px`,
        transform: getProfileTooltipTransform(input.isHovered),
        opacity: input.isHovered ? 1 : 0,
        transition: 'all 300ms ease-out',
        pointerEvents: 'none',
    };
}
function getProfileOuterWrapperStyle(hasTooltip: boolean): CSSProperties {
    return {
        display: 'inline-flex',
        flexShrink: 0,
        position: hasTooltip ? 'relative' : undefined,
    };
}
export interface CprofileimageProps {
    src?: string;
    alt?: string;
    size?: number;
    borderWidth?: number;
    borderOpacity?: number;
    onClick?: () => void;
    tooltip?: ReactNode;
    className?: string;
}
export function Cprofileimage({ src = DEFAULT_PROFILE_IMAGE_SRC, alt = DEFAULT_PROFILE_ALT, size = 64, borderWidth = 2, borderOpacity = 0.5, onClick, tooltip, className, }: CprofileimageProps): ReactElement {
    const [isHovered, setIsHovered] = useState(false);
    const hasClick = !!onClick;
    // Only route through the next/image-optimized path for local sources. External
    // URLs (e.g. supabase storage) must skip `static={true}` so the unconfigured-host
    // validator in next/image is not triggered — they render via the existing
    // <motion.img> fallback path inside Cimage with no optimization but no throw.
    const isLocal = isLocalImageSrc(src);
    const profileImage = (<Cbutton preset={ButtonPreset.Ghost} ariaLabel={hasClick ? alt : undefined} padding={0} radius={999} onClick={onClick} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} disableHoverEffects={!hasClick} style={{ width: size, height: size }} containerStyle={getProfileImageWrapperStyle({
            size,
            borderWidth,
            borderOpacity,
            isHovered,
            hasClick,
        })}>
      <Cimage src={src} alt={alt} fallbackSrc={DEFAULT_PROFILE_IMAGE_SRC} showSkeleton={true} objectFit="cover" static={isLocal} width={isLocal ? size : undefined} height={isLocal ? size : undefined} style={{
            width: '100%',
            height: '100%',
            display: 'block',
        }}/>
    </Cbutton>);
    const wrapperStyle = getProfileOuterWrapperStyle(!!tooltip);
    if (!tooltip) {
        return (
        <div className={className} style={wrapperStyle}>
        {profileImage}
      </div>
      );
    }
    // With tooltip - use absolute positioning to not affect layout
    return (
    <div className={className} style={wrapperStyle}>
      {profileImage}
      <div style={getProfileTooltipStyle({ size, isHovered })}>
        {tooltip}
      </div>
    </div>
    );
}
